import {ItemPicker} from '@github-ui/item-picker-base/ItemPicker'
import {VALUES} from '@github-ui/item-picker-base/constants/values'
import {useDebounce} from '@github-ui/use-debounce'
import {useCallback, useMemo, useState} from 'react'

import {useAlertsQuery, type AlertsQueryRequest} from '../hooks/use-alerts-query'
import styles from './AlertPicker.module.css'
import {IconButton} from '@primer/react'
import {ArrowLeftIcon} from '@primer/octicons-react'
import type {ItemGroup} from '@github-ui/item-picker-base/shared'

export interface BaseAlert {
  number: number
  repository: {
    nameWithOwner: string
  }
  title: string
}

const selectedGroup: ItemGroup = {groupId: 'selected', header: {title: 'Selected'}}
const suggestionGroup: ItemGroup = {groupId: 'suggestions', header: {title: 'Suggestions', variant: 'filled'}}

export interface AlertPickerProps<Alert> {
  anchorElement: (props: React.HTMLAttributes<HTMLElement>) => React.ReactElement

  onSelectionChange: (alerts: Alert[]) => void
  onBackClick: () => void

  currentOwner: string
  currentRepo: string
  initialAlertSelection: Alert[]

  getAlertsQueryRequest: (currentOwner: string, currentRepo: string, filter: string) => AlertsQueryRequest

  getAlertKey: (alert: Alert) => string
  getLeadingVisual: (alert: Alert) => React.ReactElement
}

export function AlertPicker<Alert extends BaseAlert>({
  anchorElement,
  onSelectionChange,
  onBackClick,
  currentOwner,
  currentRepo,
  initialAlertSelection,
  getAlertsQueryRequest,
  getAlertKey,
  getLeadingVisual,
}: AlertPickerProps<Alert>) {
  const selectedAlertKeys = initialAlertSelection.map(getAlertKey)

  const [filter, setFilter] = useState('')
  const [debouncedFilter, setDebouncedFilter] = useState('')
  const updateDebouncedFilter = useDebounce(setDebouncedFilter, VALUES.pickerDebounceTime)

  const handleFilterChange = useCallback(
    (value: string) => {
      setFilter(value)
      updateDebouncedFilter(value)
    },
    [updateDebouncedFilter],
  )

  const alertsQueryRequest = useMemo(
    () => getAlertsQueryRequest(currentOwner, currentRepo, debouncedFilter),
    [getAlertsQueryRequest, currentOwner, currentRepo, debouncedFilter],
  )
  const {data, isPending} = useAlertsQuery<Alert>(alertsQueryRequest)
  const suggestedAlerts = (data ?? []).filter(alert => !selectedAlertKeys.includes(getAlertKey(alert)))

  const allAlerts = [...initialAlertSelection, ...suggestedAlerts].map(alert => ({
    ...alert,
    __isNew__: undefined,
  }))
  const showNoMatchMessage = allAlerts.length === 0

  const convertToItemProps = useCallback(
    (alert: Alert) => {
      const isSelected = selectedAlertKeys.includes(getAlertKey(alert))
      return {
        id: getAlertKey(alert),
        text: alert.title,
        source: alert,
        sx: {wordBreak: 'break-word'},
        groupId: isSelected ? selectedGroup.groupId : suggestionGroup.groupId,
        leadingVisual: () => getLeadingVisual(alert),
        trailingVisual: () => <span className={styles.alertNumberText}>{`#${alert.number}`}</span>,
        // Show NWO when the issue is selected as it might reside in another repository than what is being queried.
        description: isSelected ? alert.repository.nameWithOwner : undefined,
        descriptionVariant: 'block' as const,
      }
    },
    [getAlertKey, selectedAlertKeys, getLeadingVisual],
  )

  return (
    <ItemPicker<Alert>
      items={allAlerts}
      groups={initialAlertSelection.length ? [selectedGroup, suggestionGroup] : [suggestionGroup]}
      initialSelectedItems={initialAlertSelection}
      getItemKey={getAlertKey}
      convertToItemProps={convertToItemProps}
      selectionVariant="multiple"
      filterItems={handleFilterChange}
      initialFilter={filter}
      onSelectionChange={onSelectionChange}
      loading={isPending}
      placeholderText="Search alerts..."
      title={
        <div className={styles.BackToRepositorySelectionButton}>
          <IconButton
            icon={ArrowLeftIcon}
            variant="invisible"
            aria-label="Back to repository selection"
            tooltipDirection="n"
            onClick={onBackClick}
          />
          <span>
            {currentOwner}/{currentRepo}
          </span>
        </div>
      }
      width="medium"
      height="large"
      suppressNoMatchItem
      improvedNoMatchAccessibility={showNoMatchMessage}
      noMatchMessage={
        showNoMatchMessage
          ? {
              title: 'No alerts found',
              body: 'Try changing your search term or selecting a different repository.',
              variant: 'empty',
            }
          : undefined
      }
      triggerOpen
      renderAnchor={anchorElement}
    />
  )
}
